// src/features/articles/api.js
import {
  getDocs,
  getDoc,
  doc,
  collection,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  updateDoc,
  increment,
  writeBatch,
  setDoc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../../shared/libs/firebaseConfig"; // ปรับ path ตามโปรเจกต์คุณ



/** helper: แปลง doc -> object พร้อม id */
function mapDoc(d) {
  const data = d.data();
  return { id: d.id, ...data };
}

/** โหลดบทความด้วย slug (ใช้ใน ArticlePage) */
export async function getArticleBySlug(slug) {
  if (!slug) return null;
  /* const q = query(
    collection(db, "articles"),
    where("slug", "==", slug),
    limit(1)
  ); */ 
  const q = query(
    collection(db, "articles"),
    where("slug", "==", slug),
    where("status", "==", "published"),
    where("publicStatus", "==", true),
    limit(1)
  );

  const snap = await getDocs(q);
  if (snap.empty) return null;

  const a = mapDoc(snap.docs[0]);

  // ถ้า date เก็บเป็น Timestamp ให้ทำ fallback เป็น string แสดงผลได้
  if (a.date && typeof a.date === "object" && a.date.toDate) {
    try {
      a.date = a.date.toDate().toLocaleDateString("th-TH", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (_) {}
  }

  // เผื่อกรณีบางฟิลด์ว่าง ให้มีค่าเริ่มต้น
  a.tagsDisplay = Array.isArray(a.tagsDisplay) ? a.tagsDisplay : [];
  a.contentBlocks = Array.isArray(a.contentBlocks) ? a.contentBlocks : [];
  return a;
}

/** บทความที่เกี่ยวข้อง (อิง subCategorySlug) */
export async function getRelatedArticles({ subCategorySlug, excludeId, limit: lim = 6 }) {
  if (!subCategorySlug) return [];
  // เรียงจากใหม่ → เก่า (ต้องมี index: categorySlug + createdAt)
  const q = query(
    collection(db, "articles"),
    where("subCategorySlug", "==", subCategorySlug),
    where("status", "==", "published"),
    where("publicStatus", "==", true),
    orderBy("createdAt", "desc"),
    limit(lim + 1) // +1 เพื่อกันชนตัวเอง
  );

  
  const snap = await getDocs(q);
  const items = snap.docs.map(mapDoc);

  // ตัดตัวเองออกถ้ามี
  const filtered = excludeId ? items.filter(a => a.id !== excludeId) : items;
  return filtered.slice(0, lim);
}

/** บทความใหม่ล่าสุด (เผื่อไปใช้หน้า Home/Newest) */
export async function getLatestArticles(lim = 6) {
  const q = query(
    collection(db, "articles"),
    // orderBy("createdAt", "desc"),
    where("status", "==", "published"),
    where("publicStatus", "==", true),
    orderBy("createdAt", "desc"),
    limit(lim)
  );
  const snap = await getDocs(q);
  return snap.docs.map(mapDoc);
}

/** เพิ่มตัวนับ views แบบง่าย (client-side) */
export async function incrementViews(articleId) {
  try {
    if (!articleId) return;
    const ref = doc(db, "articles", articleId);
    await updateDoc(ref, {
      views: increment(1),
      updatedAt: serverTimestamp(),
    });
  } catch (err) {
    // เงียบได้ใน UX จริง (ไม่ควร blocking)
    console.warn("incrementViews failed:", err?.message || err);
  }
}

export async function getPublicProfileByUid(uid) {
  if (!uid) return null;
  const ref = doc(db, "publicProfiles", uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

// --- LIKE ---
export async function toggleLike(articleId, uid) {
  if (!articleId || !uid) {
    throw new Error("toggleLike: missing articleId or uid");
  }

  const articleLikeRef = doc(db, "articles", articleId, "likes", uid);
  const userLikeRef    = doc(db, "users", uid, "likes", articleId);

  const snap = await getDoc(articleLikeRef);
  const batch = writeBatch(db);

  if (snap.exists()) {
    // เคสนี้: มี like อยู่แล้ว -> กดอีกครั้ง = ยกเลิก like
    batch.delete(articleLikeRef);
    batch.delete(userLikeRef);
    await batch.commit();
    return false; // nowLiked = false
  } else {
    // เคสนี้: ยังไม่เคย like -> สร้างใหม่ทั้งสองฝั่ง
    const payload = {
      articleId,
      uid,
      createdAt: serverTimestamp(),
    };
    batch.set(articleLikeRef, payload);
    batch.set(userLikeRef, payload);
    await batch.commit();
    return true; // nowLiked = true
  }
}

// จำนวน like ทั้งหมดของบทความ
export async function getLikeCount(articleId) {
  if (!articleId) return 0;
  const colRef = collection(db, "articles", articleId, "likes");
  const snap = await getDocs(colRef);
  return snap.size;
}

// user คนนี้กด like บทความนี้หรือยัง
export async function isLiked(articleId, uid) {
  if (!articleId || !uid) return false;
  const ref = doc(db, "articles", articleId, "likes", uid);
  const snap = await getDoc(ref);
  return snap.exists();
}

// --- BOOKMARK ---
export async function toggleBookmark(articleId, uid) {
  if (!articleId || !uid) {
    throw new Error("toggleBookmark: missing articleId or uid");
  }

  const articleBmRef = doc(db, "articles", articleId, "bookmarks", uid);
  const userBmRef    = doc(db, "users", uid, "bookmarks", articleId);

  const snap = await getDoc(articleBmRef);
  const batch = writeBatch(db);

  if (snap.exists()) {
    // มี bookmark อยู่แล้ว -> ยกเลิก
    batch.delete(articleBmRef);
    batch.delete(userBmRef);
    await batch.commit();
    return false; // nowBookmarked = false
  } else {
    // ยังไม่เคย bookmark -> สร้างใหม่ทั้งสองฝั่ง
    const payload = {
      articleId,
      uid,
      createdAt: serverTimestamp(),
    };
    batch.set(articleBmRef, payload);
    batch.set(userBmRef, payload);
    await batch.commit();
    return true; // nowBookmarked = true
  }
}

// user คนนี้ bookmark บทความนี้หรือยัง
export async function isBookmarked(articleId, uid) {
  if (!articleId || !uid) return false;
  const ref = doc(db, "articles", articleId, "bookmarks", uid);
  const snap = await getDoc(ref);
  return snap.exists();
}

// จำนวน bookmark ทั้งหมดของบทความ
export async function getBookmarkCount(articleId) {
  if (!articleId) return 0;
  const colRef = collection(db, "articles", articleId, "bookmarks");
  const snap = await getDocs(colRef);
  return snap.size;
}

/** เพิ่มตัวนับจำนวนครั้งที่เปิด AI Chat ของบทความ */
export async function incrementAiChatOpenCount(articleId) {
  try {
    if (!articleId) return;
    const ref = doc(db, "articles", articleId);
    await updateDoc(ref, {
      aiChatOpenCount: increment(1),
      updatedAt: serverTimestamp(),
    });
  } catch (err) {
    console.warn("incrementAiChatOpenCount failed:", err?.message || err);
  }
}